import {
    SolveConnectorPlacementModelMode,
    ArticleGroupPlacementLifeCycleCode,
    ArticleInstancePlacementSourceType,
    type ArticleGroupPlacement,
    type SolveConnectorPlacementModel
} from 'api/models';
import {
    buildGroupSavedPrefabId,
    convertToDegree,
    findPoiGuidsByInstanceId,
    isFramiClamp,
    mapArticleInstanceToPlacement
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { EMPTY_GUID } from 'shared/constants';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

interface ExistingPlacementsResult {
    placements: ArticleGroupPlacement[];
    connectorPlacements: ArticleGroupPlacement[];
}

function buildExistingPlacements(state: PlacementState, poiIndex: number): ExistingPlacementsResult {
    const poiData = state.pois[poiIndex];
    if (!poiData) {
        return { placements: [], connectorPlacements: [] };
    }

    const placements: ArticleGroupPlacement[] = [];
    const connectorPlacements: ArticleGroupPlacement[] = [];
    const poiGuid = poiData.poi.poiGuid;

    Object.entries(poiData.articles).forEach(([articleId, instanceIds]) => {
        const article = state.articles[articleId];
        if (!article || instanceIds.length === 0) {
            return;
        }

        const instances = instanceIds
            .map((instanceId) => {
                const instancePoiGuids = findPoiGuidsByInstanceId(state, instanceId);
                if (!instancePoiGuids.includes(poiGuid)) {
                    return null;
                }

                const articleInstance = article.articleInstances[instanceId];
                if (!articleInstance) {
                    return null;
                }

                const groupId = state.instanceToGroupMap[instanceId] ?? null;
                const groupName = groupId ? (state.articleGroups[groupId]?.name ?? null) : null;
                const sourceSavedGroupPrefabId = groupId
                    ? (buildGroupSavedPrefabId(state, groupId) ?? articleInstance.sourceSavedGroupPrefabId ?? null)
                    : (articleInstance.sourceSavedGroupPrefabId ?? null);

                let sourceType: ArticleInstancePlacementSourceType | null = null;
                if (groupId) {
                    sourceType = sourceSavedGroupPrefabId
                        ? ArticleInstancePlacementSourceType.SavedGroup
                        : ArticleInstancePlacementSourceType.UnsavedGroup;
                }

                const isLocked =
                    groupId !== null || sourceSavedGroupPrefabId !== null || articleInstance.isLocked === true;
                const placement = mapArticleInstanceToPlacement(instanceId, articleInstance, instancePoiGuids, false);

                return {
                    ...placement,
                    parts: placement.parts.map((part) => ({ ...part, rotation: convertToDegree(part.rotation) })),
                    isLocked,
                    sourceType,
                    sourceGroupId: groupId ?? articleInstance.sourceGroupId ?? null,
                    sourceGroupName: groupName ?? articleInstance.sourceGroupName ?? null,
                    sourceGroupInstanceId: articleInstance.sourceGroupInstanceId ?? null,
                    sourceSavedGroupPrefabId
                };
            })
            .filter((instance) => instance !== null);

        if (instances.length === 0) {
            return;
        }

        if (isFramiClamp(article.articleNumber)) {
            connectorPlacements.push({
                articleGuid: articleId,
                articleNumber: article.articleNumber,
                lifeCycleCode: article.lifeCycleCode as ArticleGroupPlacementLifeCycleCode,
                representationGuid: EMPTY_GUID,
                instances
            });
        } else {
            placements.push({
                articleGuid: articleId,
                articleNumber: article.articleNumber,
                lifeCycleCode: article.lifeCycleCode as ArticleGroupPlacementLifeCycleCode,
                representationGuid: EMPTY_GUID,
                instances
            });
        }
    });

    return { placements, connectorPlacements };
}

export interface BuildSolveConnectorPlacementRequestOptions {
    state: PlacementState;
    sessionId: string;
    systemId: string;
    poiIndex: number;
    mode?: SolveConnectorPlacementModel['mode'];
    selectedConnectorArticleGuid?: string | null;
}

export function buildSolveConnectorPlacementRequest(
    options: BuildSolveConnectorPlacementRequestOptions
): SolveConnectorPlacementModel {
    const { state, sessionId, systemId, poiIndex } = options;

    const poiId = state.pois[poiIndex]?.poi.poiGuid ?? EMPTY_GUID;
    const { placements, connectorPlacements } = buildExistingPlacements(state, poiIndex);

    return {
        sessionId,
        poiId,
        systemId,
        existingPlacements: placements,
        existingConnectorPlacements: connectorPlacements,
        mode: options.mode ?? SolveConnectorPlacementModelMode.Automatic,
        selectedConnectorArticleGuid: options.selectedConnectorArticleGuid
    };
}
